import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Building2, Store, MapPin, Euro, Star, Filter, ArrowUpRight, ArrowDownRight, BarChart3, Maximize2 } from 'lucide-react';
import { KPICard } from '../components/ui/KPICard';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ZAxis, ComposedChart, Line, Bar, Legend, Cell } from 'recharts';
// Level options
const levels = [{
  id: 'konzern',
  name: 'Konzern-Übersicht',
  icon: Building2
}, {
  id: 'region',
  name: 'Regionen',
  icon: MapPin
}, {
  id: 'markt',
  name: 'Märkte',
  icon: Store
}];
// Market data with revenue, rating, and size
const marketsData = [{
  id: 'hamburg-hafencity',
  name: 'EDEKA Center Hamburg-Hafencity',
  region: 'Nord',
  rating: 4.3,
  revenue: 8200,
  size: 2800,
  revenuePerSqm: 2928
}, {
  id: 'berlin-kreuzberg',
  name: 'EDEKA Neukauf Berlin-Kreuzberg',
  region: 'Minden-Hannover',
  rating: 3.8,
  revenue: 4100,
  size: 1200,
  revenuePerSqm: 3417
}, {
  id: 'muenchen-schwabing',
  name: 'EDEKA München-Schwabing',
  region: 'Südbayern',
  rating: 4.6,
  revenue: 6800,
  size: 1800,
  revenuePerSqm: 3778
}, {
  id: 'koeln-ehrenfeld',
  name: 'EDEKA Köln-Ehrenfeld',
  region: 'Rhein-Ruhr',
  rating: 3.5,
  revenue: 3200,
  size: 1100,
  revenuePerSqm: 2909
}, {
  id: 'frankfurt-sachsenhausen',
  name: 'EDEKA Frankfurt-Sachsenhausen',
  region: 'Hessenring',
  rating: 4.4,
  revenue: 5900,
  size: 1600,
  revenuePerSqm: 3688
}, {
  id: 'dortmund-innenstadt',
  name: 'EDEKA Dortmund-Innenstadt',
  region: 'Rhein-Ruhr',
  rating: 3.9,
  revenue: 4500,
  size: 1400,
  revenuePerSqm: 3214
}, {
  id: 'leipzig-sued',
  name: 'EDEKA Center Leipzig-Süd',
  region: 'Nordbayern',
  rating: 4.5,
  revenue: 7200,
  size: 2200,
  revenuePerSqm: 3273
}, {
  id: 'stuttgart-west',
  name: 'EDEKA Stuttgart-West',
  region: 'Südwest',
  rating: 4.2,
  revenue: 5100,
  size: 1500,
  revenuePerSqm: 3400
}, {
  id: 'freiburg-zentrum',
  name: 'EDEKA Freiburg-Zentrum',
  region: 'Südwest',
  rating: 4.1,
  revenue: 4800,
  size: 1350,
  revenuePerSqm: 3556
}, {
  id: 'kiel-zentrum',
  name: 'EDEKA Kiel-Zentrum',
  region: 'Nord',
  rating: 4.4,
  revenue: 5500,
  size: 1650,
  revenuePerSqm: 3333
}, {
  id: 'hannover-list',
  name: 'EDEKA Hannover-List',
  region: 'Minden-Hannover',
  rating: 4.0,
  revenue: 4200,
  size: 1300,
  revenuePerSqm: 3231
}, {
  id: 'nuernberg-zentrum',
  name: 'EDEKA Nürnberg-Zentrum',
  region: 'Nordbayern',
  rating: 4.3,
  revenue: 5800,
  size: 1700,
  revenuePerSqm: 3412
}, {
  id: 'dresden-altstadt',
  name: 'EDEKA Dresden-Altstadt',
  region: 'Nordbayern',
  rating: 4.1,
  revenue: 4400,
  size: 1250,
  revenuePerSqm: 3520
}, {
  id: 'duesseldorf-bilk',
  name: 'EDEKA Düsseldorf-Bilk',
  region: 'Rhein-Ruhr',
  rating: 4.2,
  revenue: 5200,
  size: 1550,
  revenuePerSqm: 3355
}, {
  id: 'bremen-mitte',
  name: 'EDEKA Bremen-Mitte',
  region: 'Nord',
  rating: 3.9,
  revenue: 3800,
  size: 1150,
  revenuePerSqm: 3304
}];
// Region aggregated data
const regionsData = [{
  id: 'nord',
  name: 'EDEKA Nord',
  rating: 4.2,
  revenue: 17500,
  size: 5600,
  revenuePerSqm: 3125,
  markets: 3
}, {
  id: 'minden-hannover',
  name: 'EDEKA Minden-Hannover',
  rating: 3.9,
  revenue: 8300,
  size: 2500,
  revenuePerSqm: 3320,
  markets: 2
}, {
  id: 'rhein-ruhr',
  name: 'EDEKA Rhein-Ruhr',
  rating: 3.87,
  revenue: 12900,
  size: 4050,
  revenuePerSqm: 3185,
  markets: 3
}, {
  id: 'hessenring',
  name: 'EDEKA Hessenring',
  rating: 4.4,
  revenue: 5900,
  size: 1600,
  revenuePerSqm: 3688,
  markets: 1
}, {
  id: 'nordbayern',
  name: 'EDEKA Nordbayern-Sachsen-Thüringen',
  rating: 4.3,
  revenue: 17400,
  size: 5150,
  revenuePerSqm: 3379,
  markets: 3
}, {
  id: 'suedwest',
  name: 'EDEKA Südwest',
  rating: 4.15,
  revenue: 9900,
  size: 2850,
  revenuePerSqm: 3474,
  markets: 2
}, {
  id: 'suedbayern',
  name: 'EDEKA Südbayern',
  rating: 4.6,
  revenue: 6800,
  size: 1800,
  revenuePerSqm: 3778,
  markets: 1
}];
// Konzern data
const konzernData = {
  name: 'EDEKA Gesamtkonzern',
  rating: 4.15,
  revenue: 78700,
  size: 23550,
  revenuePerSqm: 3342,
  markets: 15,
  regions: 7
};
// Time series data
const timeSeriesData = [{
  month: 'Feb 23',
  rating: 4.05,
  revenue: 72500,
  revenuePerSqm: 3078
}, {
  month: 'Mär 23',
  rating: 4.08,
  revenue: 73200,
  revenuePerSqm: 3108
}, {
  month: 'Apr 23',
  rating: 4.06,
  revenue: 72800,
  revenuePerSqm: 3091
}, {
  month: 'Mai 23',
  rating: 4.1,
  revenue: 74500,
  revenuePerSqm: 3163
}, {
  month: 'Jun 23',
  rating: 4.12,
  revenue: 75800,
  revenuePerSqm: 3219
}, {
  month: 'Jul 23',
  rating: 4.09,
  revenue: 74200,
  revenuePerSqm: 3151
}, {
  month: 'Aug 23',
  rating: 4.11,
  revenue: 75100,
  revenuePerSqm: 3189
}, {
  month: 'Sep 23',
  rating: 4.14,
  revenue: 76500,
  revenuePerSqm: 3249
}, {
  month: 'Okt 23',
  rating: 4.13,
  revenue: 76800,
  revenuePerSqm: 3261
}, {
  month: 'Nov 23',
  rating: 4.16,
  revenue: 77500,
  revenuePerSqm: 3291
}, {
  month: 'Dez 23',
  rating: 4.18,
  revenue: 78200,
  revenuePerSqm: 3321
}, {
  month: 'Jan 24',
  rating: 4.2,
  revenue: 78700,
  revenuePerSqm: 3342
}];
// Calculate correlation coefficient
const calculateCorrelation = (data: Array<{
  rating: number;
  revenuePerSqm: number;
}>) => {
  const n = data.length;
  const sumX = data.reduce((acc, d) => acc + d.rating, 0);
  const sumY = data.reduce((acc, d) => acc + d.revenuePerSqm, 0);
  const sumXY = data.reduce((acc, d) => acc + d.rating * d.revenuePerSqm, 0);
  const sumX2 = data.reduce((acc, d) => acc + d.rating * d.rating, 0);
  const sumY2 = data.reduce((acc, d) => acc + d.revenuePerSqm * d.revenuePerSqm, 0);
  const numerator = n * sumXY - sumX * sumY;
  const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
  return denominator === 0 ? 0 : numerator / denominator;
};
// Custom tooltip for scatter chart
const CustomScatterTooltip = ({
  active,
  payload
}: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 text-sm" data-id="element-1370">
        <div className="font-semibold text-gray-900 mb-2" data-id="element-1371">{data.name}</div>
        <div className="space-y-1" data-id="element-1372">
          <div className="flex justify-between gap-4" data-id="element-1373">
            <span className="text-gray-500" data-id="element-1374">Rating:</span>
            <span className="font-medium" data-id="element-1375">{data.rating.toFixed(1)} ★</span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-1376">
            <span className="text-gray-500" data-id="element-1377">Umsatz/m²:</span>
            <span className="font-medium" data-id="element-1378">
              {data.revenuePerSqm.toLocaleString('de-DE')} €
            </span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-1379">
            <span className="text-gray-500" data-id="element-1380">Gesamtumsatz:</span>
            <span className="font-medium" data-id="element-1381">
              {(data.revenue / 1000).toFixed(1)}M €
            </span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-1382">
            <span className="text-gray-500" data-id="element-1383">Fläche:</span>
            <span className="font-medium" data-id="element-1384">
              {data.size.toLocaleString('de-DE')} m²
            </span>
          </div>
        </div>
      </div>;
  }
  return null;
};
export function StrategicAdvisorPage() {
  const [selectedLevel, setSelectedLevel] = useState<'konzern' | 'region' | 'markt'>('markt');
  // Get data based on selected level
  const getScatterData = () => {
    switch (selectedLevel) {
      case 'markt':
        return marketsData.map(m => ({
          ...m,
          z: m.size
        }));
      case 'region':
        return regionsData.map(r => ({
          ...r,
          z: r.size
        }));
      case 'konzern':
        return [{
          ...konzernData,
          z: konzernData.size
        }];
    }
  };
  const scatterData = getScatterData();
  const correlation = calculateCorrelation(scatterData);
  // Find top and bottom performers
  const sortedByEfficiency = [...scatterData].sort((a, b) => b.revenuePerSqm - a.revenuePerSqm);
  const topPerformers = sortedByEfficiency.slice(0, 3);
  const bottomPerformers = sortedByEfficiency.slice(-3).reverse();
  // Calculate potential
  const avgRevenuePerSqm = scatterData.reduce((acc, d) => acc + d.revenuePerSqm, 0) / scatterData.length;
  const potentialGain = scatterData.filter(d => d.revenuePerSqm < avgRevenuePerSqm).reduce((acc, d) => acc + (avgRevenuePerSqm - d.revenuePerSqm) * d.size / 1000, 0);
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-1385">
      {/* Level Filter */}
      <Card className="!p-4" data-id="element-1386">
        <div className="flex items-center gap-4 flex-wrap" data-id="element-1387">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-1388">
            <Filter size={16} data-id="element-1389" />
            <span data-id="element-1390">Ebene:</span>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-1391">
            {levels.map(level => {
            const isSelected = selectedLevel === level.id;
            const Icon = level.icon;
            return <button key={level.id} onClick={() => setSelectedLevel(level.id as any)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${isSelected ? 'bg-edeka-blue text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-1392">
                  <Icon size={14} data-id="element-1393" />
                  {level.name}
                </button>;
          })}
          </div>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1394">
        <KPICard title="Korrelation Rating ↔ Umsatz" value={correlation.toFixed(2)} change={correlation > 0.7 ? 'Stark positiv' : correlation > 0.4 ? 'Moderat' : 'Schwach'} trend="up" icon={<TrendingUp size={18} data-id="element-1396" />} subtext="Pearson-Koeffizient" data-id="element-1395" />
        <KPICard title="Ø Umsatz pro m²" value={`${avgRevenuePerSqm.toLocaleString('de-DE', {
        maximumFractionDigits: 0
      })} €`} change="+4.2%" trend="up" icon={<Euro size={18} data-id="element-1398" />} subtext="vs. Vorjahr" data-id="element-1397" />
        <KPICard title="Ø Bewertung" value={(scatterData.reduce((acc, d) => acc + d.rating, 0) / scatterData.length).toFixed(2)} change="+0.15" trend="up" icon={<Star size={18} data-id="element-1400" />} subtext="Google Rating" data-id="element-1399" />
        <KPICard title="Optimierungspotenzial" value={`${potentialGain.toFixed(1)}M €`} change="bei Ø-Effizienz" trend="up" icon={<BarChart3 size={18} data-id="element-1402" />} subtext="Jahresumsatz" data-id="element-1401" />
      </div>

      {/* Main Scatter Chart */}
      <Card data-id="element-1403">
        <div className="p-6" data-id="element-1404">
          <div className="flex items-center justify-between mb-6" data-id="element-1405">
            <div data-id="element-1406">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1407">
                Rating vs. Umsatzeffizienz
              </h3>
              <p className="text-sm text-gray-500" data-id="element-1408">
                Blasengröße = Verkaufsfläche (m²) •{' '}
                {selectedLevel === 'markt' ? 'Märkte' : selectedLevel === 'region' ? 'Regionen' : 'Konzern'}
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-500" data-id="element-1409">
              <div className="flex items-center gap-1" data-id="element-1410">
                <div className="w-3 h-3 rounded-full bg-edeka-blue opacity-30" data-id="element-1411"></div>
                <span data-id="element-1412">Klein</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-1413">
                <div className="w-4 h-4 rounded-full bg-edeka-blue opacity-50" data-id="element-1414"></div>
                <span data-id="element-1415">Mittel</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-1416">
                <div className="w-5 h-5 rounded-full bg-edeka-blue opacity-70" data-id="element-1417"></div>
                <span data-id="element-1418">Groß</span>
              </div>
            </div>
          </div>

          <div className="h-96" data-id="element-1419">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1420">
              <ScatterChart margin={{
              top: 20,
              right: 20,
              bottom: 20,
              left: 20
            }} data-id="element-1421">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1422" />
                <XAxis type="number" dataKey="rating" name="Rating" domain={[3.2, 5]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} label={{
                value: 'Google Rating (Sterne)',
                position: 'bottom',
                offset: 0,
                style: {
                  fontSize: 11,
                  fill: '#6b7280'
                }
              }} data-id="element-1423" />
                <YAxis type="number" dataKey="revenuePerSqm" name="Umsatz/m²" domain={[2500, 4000]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} tickFormatter={value => `${(value / 1000).toFixed(1)}k`} label={{
                value: 'Umsatz pro m² (€)',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fontSize: 11,
                  fill: '#6b7280'
                }
              }} data-id="element-1424" />
                <ZAxis type="number" dataKey="z" range={[100, 1000]} data-id="element-1425" />
                <Tooltip content={<CustomScatterTooltip data-id="element-1427" />} data-id="element-1426" />
                <Scatter name="Standorte" data={scatterData} fill="#003051" data-id="element-1428">
                  {scatterData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.rating >= 4.3 ? '#639754' : entry.rating >= 3.8 ? '#003051' : '#E03C32'} fillOpacity={0.7} data-id="element-1429" />)}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>

          {/* Trend line info */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm" data-id="element-1430">
            <div className="flex items-center gap-2" data-id="element-1431">
              <TrendingUp size={16} style={{
              color: '#639754'
            }} data-id="element-1432" />
              <span className="text-gray-600" data-id="element-1433">
                Korrelation:{' '}
                <span className="font-semibold" style={{
                color: correlation > 0.5 ? '#639754' : '#FFD301'
              }} data-id="element-1434">
                  {(correlation * 100).toFixed(0)}%
                </span>{' '}
                – Höhere Bewertungen korrelieren mit höherem Umsatz pro m²
              </span>
            </div>
            <div className="text-gray-500" data-id="element-1435">
              {scatterData.length}{' '}
              {selectedLevel === 'markt' ? 'Märkte' : selectedLevel === 'region' ? 'Regionen' : 'Datenpunkt'}
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1436">
        {/* Top Performers */}
        <Card data-id="element-1437">
          <div className="p-6" data-id="element-1438">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-1439">
              <ArrowUpRight className="text-green-600" size={20} data-id="element-1440" />
              Top Performer
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-1441">
              Höchste Umsatzeffizienz (€/m²)
            </p>

            <div className="space-y-3" data-id="element-1442">
              {topPerformers.map((item, index) => <motion.div key={item.id || item.name} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1
            }} className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#e8f0e5'
            }} data-id="element-1443">
                  <div className="flex items-center gap-3" data-id="element-1444">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                  backgroundColor: '#639754'
                }} data-id="element-1445">
                      {index + 1}
                    </div>
                    <div data-id="element-1446">
                      <div className="font-medium text-gray-900 text-sm" data-id="element-1447">
                        {item.name}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1448">
                        {item.rating.toFixed(1)} ★ •{' '}
                        {item.size.toLocaleString('de-DE')} m²
                      </div>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-1449">
                    <div className="font-bold text-lg" style={{
                  color: '#639754'
                }} data-id="element-1450">
                      {item.revenuePerSqm.toLocaleString('de-DE')} €
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-1451">pro m²</div>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </Card>

        {/* Bottom Performers */}
        <Card data-id="element-1452">
          <div className="p-6" data-id="element-1453">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-1454">
              <ArrowDownRight className="text-red-600" size={20} data-id="element-1455" />
              Optimierungspotenzial
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-1456">
              Niedrigste Umsatzeffizienz (€/m²)
            </p>

            <div className="space-y-3" data-id="element-1457">
              {bottomPerformers.map((item, index) => <motion.div key={item.id || item.name} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1
            }} className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#fce8e7'
            }} data-id="element-1458">
                  <div className="flex items-center gap-3" data-id="element-1459">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                  backgroundColor: '#E03C32'
                }} data-id="element-1460">
                      {scatterData.length - index}
                    </div>
                    <div data-id="element-1461">
                      <div className="font-medium text-gray-900 text-sm" data-id="element-1462">
                        {item.name}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1463">
                        {item.rating.toFixed(1)} ★ •{' '}
                        {item.size.toLocaleString('de-DE')} m²
                      </div>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-1464">
                    <div className="font-bold text-lg" style={{
                  color: '#E03C32'
                }} data-id="element-1465">
                      {item.revenuePerSqm.toLocaleString('de-DE')} €
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-1466">
                      pro m² (
                      {((avgRevenuePerSqm - item.revenuePerSqm) / avgRevenuePerSqm * 100).toFixed(0)}
                      % unter Ø)
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </Card>
      </div>

      {/* Time Series Chart */}
      <Card data-id="element-1467">
        <div className="p-6" data-id="element-1468">
          <div className="flex items-center justify-between mb-6" data-id="element-1469">
            <div data-id="element-1470">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1471">
                Entwicklung über Zeit
              </h3>
              <p className="text-sm text-gray-500" data-id="element-1472">
                Konzern-Umsatz und Durchschnittsbewertung (12 Monate)
              </p>
            </div>
            <div className="flex items-center gap-4 text-sm" data-id="element-1473">
              <div className="flex items-center gap-2" data-id="element-1474">
                <div className="w-3 h-3 rounded-full bg-edeka-blue" data-id="element-1475"></div>
                <span className="text-gray-600" data-id="element-1476">Umsatz (Mio. €)</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1477">
                <div className="w-6 h-0.5 bg-edeka-yellow" data-id="element-1478"></div>
                <span className="text-gray-600" data-id="element-1479">Rating</span>
              </div>
            </div>
          </div>

          <div className="h-72" data-id="element-1480">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1481">
              <ComposedChart data={timeSeriesData} data-id="element-1482">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-1483" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} data-id="element-1484" />
                <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} domain={[70000, 82000]} tickFormatter={value => `${(value / 1000).toFixed(0)}`} label={{
                value: 'Umsatz (Mio. €)',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fontSize: 11,
                  fill: '#9ca3af'
                }
              }} data-id="element-1485" />
                <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} domain={[3.9, 4.4]} label={{
                value: 'Rating',
                angle: 90,
                position: 'insideRight',
                style: {
                  fontSize: 11,
                  fill: '#9ca3af'
                }
              }} data-id="element-1486" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }} formatter={(value: number, name: string) => [name === 'revenue' ? `${(value / 1000).toFixed(1)} Mio. €` : value.toFixed(2), name === 'revenue' ? 'Umsatz' : 'Rating']} data-id="element-1487" />
                <Bar yAxisId="left" dataKey="revenue" fill="#003051" radius={[4, 4, 0, 0]} barSize={30} data-id="element-1488" />
                <Line yAxisId="right" type="monotone" dataKey="rating" stroke="#FFD200" strokeWidth={3} dot={{
                fill: '#FFD200',
                strokeWidth: 2,
                r: 4
              }} data-id="element-1489" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Summary */}
          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-4 text-center" data-id="element-1490">
            <div data-id="element-1491">
              <div className="text-xs text-gray-500 mb-1" data-id="element-1492">Umsatzwachstum</div>
              <div className="text-lg font-bold" style={{
              color: '#639754'
            }} data-id="element-1493">
                +8.5%
              </div>
            </div>
            <div data-id="element-1494">
              <div className="text-xs text-gray-500 mb-1" data-id="element-1495">
                Rating-Verbesserung
              </div>
              <div className="text-lg font-bold" style={{
              color: '#639754'
            }} data-id="element-1496">
                +0.15
              </div>
            </div>
            <div data-id="element-1497">
              <div className="text-xs text-gray-500 mb-1" data-id="element-1498">
                Effizienzsteigerung
              </div>
              <div className="text-lg font-bold" style={{
              color: '#639754'
            }} data-id="element-1499">
                +8.6%
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Data Table */}
      <Card data-id="element-1500">
        <div className="p-6" data-id="element-1501">
          <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1502">
            Detailübersicht{' '}
            {selectedLevel === 'markt' ? 'Märkte' : selectedLevel === 'region' ? 'Regionen' : 'Konzern'}
          </h3>

          <div className="overflow-x-auto" data-id="element-1503">
            <table className="w-full text-sm" data-id="element-1504">
              <thead data-id="element-1505">
                <tr className="border-b border-gray-200" data-id="element-1506">
                  <th className="text-left py-3 px-4 font-medium text-gray-500" data-id="element-1507">
                    Name
                  </th>
                  {selectedLevel === 'markt' && <th className="text-left py-3 px-4 font-medium text-gray-500" data-id="element-1508">
                      Region
                    </th>}
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1509">
                    Rating
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1510">
                    Umsatz
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1511">
                    Fläche
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1512">
                    €/m²
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1513">
                    vs. Ø
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1514">
                {scatterData.map((item: any, index) => {
                const diff = (item.revenuePerSqm - avgRevenuePerSqm) / avgRevenuePerSqm * 100;
                return <tr key={item.id || index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1515">
                      <td className="py-3 px-4 font-medium text-gray-900" data-id="element-1516">
                        {item.name}
                      </td>
                      {selectedLevel === 'markt' && <td className="py-3 px-4 text-gray-500" data-id="element-1517">
                          {item.region}
                        </td>}
                      <td className="py-3 px-4 text-right" data-id="element-1518">
                        <span className={`font-medium ${item.rating >= 4.3 ? 'text-green-600' : item.rating >= 3.8 ? 'text-gray-900' : 'text-red-600'}`} data-id="element-1519">
                          {item.rating.toFixed(1)} ★
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900" data-id="element-1520">
                        {(item.revenue / 1000).toFixed(1)}M €
                      </td>
                      <td className="py-3 px-4 text-right text-gray-500" data-id="element-1521">
                        {item.size.toLocaleString('de-DE')} m²
                      </td>
                      <td className="py-3 px-4 text-right font-medium text-gray-900" data-id="element-1522">
                        {item.revenuePerSqm.toLocaleString('de-DE')} €
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-1523">
                        <span className={`font-medium ${diff >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-1524">
                          {diff >= 0 ? '+' : ''}
                          {diff.toFixed(1)}%
                        </span>
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>;
}